import * as React from "react";
import {
    Toolbar,
    Button,
    ButtonGroup,
    SplitButton,
    SplitButtonItem,
    SplitButtonItemClickEvent
} from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Loader } from "@progress/kendo-react-indicators";

import {
    Grid,
    GridColumn,
    GridItemChangeEvent,
    GridFilterChangeEvent,
    GridRowProps, GridCustomRowProps,
 } from '@progress/kendo-react-grid';
import {
    filterBy,
    CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import { EditDescriptor } from '@progress/kendo-react-data-tools';

import NavigateToLogin from "../NavigateToLogin";
import InventoryNew from "./InventoryNew";
import InventorySummaryGrid from "../Reports/InventorySummaryGrid";

import { useGetInventoryQuery } from '../../store/sunbright-api';
import { useEditInventoryMutation } from '../../store/sunbright-api';
import { useDeleteInventoryMutation } from '../../store/sunbright-api';
import { useAppSelector } from "../../store/store";

interface InventoryRow {
    uid: number;
    regionid?: number;
    regionname?: string;
    eventid?: number;
    productid?: number;
    boxes?: number;
    units?: number;
    expirytime?: string;
    min?: number;
    max?: number;
}

const DATA_ITEM_KEY = 'uid';

const initialFilter: CompositeFilterDescriptor = {
    logic: "and",
    filters: []
};

const regions:string[] = ["", "Test", "Ottawa", "Toronto", "Winnipeg", "Saskatoon", "Halifax", "Vancouver", "Whitehorse", "York", "Kingston", "Kelowna", "Simcoe"];

const CustomRow = (props: GridCustomRowProps) => {

    const count:number = (props.dataItem.boxes * props.dataItem.unitsperbox) + props.dataItem.units;
    const low:boolean = (props.dataItem.min > 0) && (count < props.dataItem.min);

    const noBgr = { backgroundColor: '' };
    const lowBgr = { backgroundColor: '#E799A3', fontWeight: 'bold' };

    return (
        <tr {...props.trProps} style={low ? lowBgr : noBgr}>
            {props.children}
        </tr>
    );
};

const Inventory = () => {
    const auth = useAppSelector((state) => state.auth);
    const role = (auth && (auth.user !== undefined) && auth.user.role) ? auth.user.role : "";

    const ActionCell = (props: any) => {
        const { dataItem } = props;

        return (
            <td className="k-command-cell">
                <Button
                    icon="trash"
                    style={{ background: "center" }}
                    onClick={(e) => deleteClicked(dataItem)}
                >
                </Button>
            </td>
        );
    };

    const { data: inventory, error: inventoryError, isLoading: inventoryLoading } = useGetInventoryQuery("");
    const [inventoryData, setInventoryData] = React.useState<any>(null);

    const [editInventory, editResponse] = useEditInventoryMutation();
    const [deleteInventory, deleteResponse] = useDeleteInventoryMutation();

    const [gridHeight, setGridHeight] = React.useState<string>("600px");

    const [dirtyRow, setDirtyRow] = React.useState<InventoryRow>({ uid: 0 });
    const [timerID, setTimerID] = React.useState<number>(-1);

    const [filterState, setFilterState] = React.useState(initialFilter);

    const [report, setReport] = React.useState<boolean>(false);
    const [confirmDelete, setConfirmDelete] = React.useState<boolean>(false);
    const [inventoryToDelete, setInventoryToDelete] = React.useState<any>();
    const [newItemRegion, setNewItemRegion] = React.useState<string>("");
    const [inventoryAdmin, setInventoryAdmin] = React.useState<boolean>(role.indexOf("Inventory") !== -1);

    React.useEffect(() => {
        window.addEventListener("resize", handleResize);
        setGridHeight((window.innerHeight - 100) + "px");
    }, []);

    const handleResize = () => {
        setGridHeight((window.innerHeight - 100) + "px");
    };

    React.useEffect(() => {
        if (inventory) {
            setInventoryData(inventory);
            setEdit(allInEdit(inventory));
       }
    }, [inventory]);

    const [edit, setEdit] = React.useState<EditDescriptor>({});

    interface EditSpec {
        [key: number]: string[];
    }

    const allInEdit = (data: any) => {
        let result:EditSpec = {};
        if (data && data.length > 0) {
            const editedColumns = Object.keys(data[0]); //the column names
            data.forEach((row:InventoryRow) => {
                result[row.uid] = editedColumns;
            });
        }

        return (result as EditDescriptor);
    };

    const updateInventory = (row: any) => {

        editInventory(row)
            .unwrap()
            .then((payload) => { })
            .catch((error) => {
                console.log(error)
            });
    };

    const timedOut = (row:InventoryRow) => {
        console.log("timeOut");
        if (row && (row.uid > 0)) {
            updateInventory(row);
        }
    };

    const handleItemChange = (event: GridItemChangeEvent) => {
        if (timerID >= 0) {
            window.clearTimeout(timerID);
        }

        const newData = inventoryData.map((row: InventoryRow) => {
            if (row.uid === event.dataItem.uid) {
                const newRow: InventoryRow = { ...row, [event.field!]: event.value };

                if (dirtyRow && (dirtyRow.uid > 0) && (dirtyRow.uid !== newRow.uid)) {
                    updateInventory(dirtyRow);
                    setDirtyRow({ uid: 0 });
                }

                setDirtyRow(newRow);
                setTimerID(window.setTimeout(() => timedOut(newRow), 1500));

                return newRow;
            }
            else {
                return row;
            }
        });

        setInventoryData(newData);

    };

    const handleFilterChange = (event: GridFilterChangeEvent) => {
        setFilterState(event.filter);

        // Focus normally goes to first cell in grid after rendering filter results
        const element: HTMLInputElement = event?.nativeEvent?.srcElement;
        if (element) {
            window.setTimeout(() => element.focus(), 100);
        }
    }

    const deleteClicked = (dataItem: any) => {
        setInventoryToDelete(dataItem);
        setConfirmDelete(true);
    };

    const deleteSelectedInventory = (dataItem: any) => {

        if (dataItem && dataItem.uid && dataItem.regionid) {
            const deleteProduct:any = {
                uid: dataItem.uid,
                regionid: dataItem.regionid
            };

            deleteInventory(deleteProduct)
                .unwrap()
                .then((payload) => { })
                .catch((error) => {
                    console.log(error)
                });
        }
        setConfirmDelete(false);
    };

    return (
        <React.Fragment>
            {(newItemRegion && (newItemRegion !== "")) ?
                (<div>
                    <InventoryNew
                        onReturn={() => setNewItemRegion("")}
                        regionID={regions.indexOf(newItemRegion)}
                        regionName={newItemRegion}
                    />
                </div>)
                :
                (report ?
                    <InventorySummaryGrid
                        returnLabel="Return to Inventory"
                        onReturn={() => setReport(false)}
                    />
                    :
                    <div>
                        <div className="sds-gridHeader">Inventory</div>
                        {inventoryError ? (<NavigateToLogin />) : inventoryLoading ? <Loader size="large" type="converging-spinner" /> : inventory ? (
                            <div>
                                <div>
                                    {(confirmDelete) &&
                                        <Dialog title={<div><span className="k-icon k-font-icon k-i-info" style={{ color: "green" }} ></span>&nbsp;Please confirm</div>}
                                            onClose={(e) => setConfirmDelete(false)}>
                                            <p style={{ margin: "25px", textAlign: "center" }}>
                                                Are you sure you want to delete <br />{inventoryToDelete?.description} ?
                                            </p>
                                            <DialogActionsBar>
                                                <button
                                                    className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                                                    onClick={(e) => deleteSelectedInventory(inventoryToDelete)}
                                                >
                                                    Yes
                                                </button>
                                                <button
                                                    className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                                                    onClick={(e) => setConfirmDelete(false)}
                                                >
                                                    No
                                                </button>
                                            </DialogActionsBar>
                                        </Dialog>
                                    }
                                </div>
                                {inventoryAdmin ?
                                    (<div style={{ position: "fixed", top: "46px", right: "64px", zIndex: "99" }}>
                                        <ButtonGroup>
                                            <SplitButton themeColor={"primary"}
                                                text="New Item"
                                                icon="plus-circle"
                                                onButtonClick={() => setNewItemRegion(regions[auth.user.region])}
                                                onItemClick={(e: SplitButtonItemClickEvent) => setNewItemRegion(e.item.text)}>
                                                <SplitButtonItem text="Halifax" />
                                                <SplitButtonItem text="Kelowna" />
                                                <SplitButtonItem text="Kingston" />
                                                <SplitButtonItem text="Ottawa" />
                                                <SplitButtonItem text="Saskatoon" />
                                                <SplitButtonItem text="Simcoe" />
                                                <SplitButtonItem text="Toronto" />
                                                <SplitButtonItem text="Vancouver" />
                                                <SplitButtonItem text="Whitehorse" />
                                                <SplitButtonItem text="Winnipeg" />
                                                <SplitButtonItem text="York" />
                                            </SplitButton>
                                            <Button
                                                themeColor={"primary"}
                                                icon="file-report"
                                                style={{ borderLeftColor: "white", borderLeftWidth: "1px" }}
                                                onClick={() => setReport(true)}>
                                                Report
                                            </Button>
                                        </ButtonGroup>
                                    </div>)
                                    :
                                    (<Button
                                        themeColor={"primary"}
                                        icon="plus-circle"
                                        onClick={() => setNewItemRegion(regions[auth.user.region])}
                                        style={{ position: "absolute", top: "46px", right: "64px", width: "128px", zIndex: "99" }}>
                                        New Item
                                    </Button>)
                                }
                                <Grid
                                    style={{ height: gridHeight }}
                                    className="inventoryGrid"
                                    dataItemKey={DATA_ITEM_KEY}
                                    pageable={false}
                                    data={filterBy(inventoryData, filterState)}
                                    //autoProcessData={true}
                                    filterable={true}
                                    filter={filterState}
                                    onFilterChange={handleFilterChange}
                                    resizable={true}
                                    edit={edit}
                                    editable={true}
                                    onItemChange={handleItemChange}
                                    rows={{ data: CustomRow }}
                                //navigatable={{ mode: 'incell' as NavigatableMode }}
                                >
                                    <GridColumn field="uid" filterable={false} title=" " editable={false} width="0px" />
                                    <GridColumn field="productid" title="Product ID" editable={false} width="150px" />
                                    <GridColumn field="supplier" title="Supplier" editable={false} width="181px" />
                                    <GridColumn field="description" title="Description" editable={false} width="451px" />
                                    <GridColumn field="unitsperbox" title="Units Per Box" filterable={false} editable={false} width="126px" />
                                    <GridColumn field="boxes" title="Boxes" filterable={false} editor="numeric" width="100px" />
                                    <GridColumn field="units" title="Units" filterable={false} editor="numeric" width="95px" />
                                    <GridColumn field="expirytime" title="Expiry" filterable={false} editor="date" format="{0:d}" width="136px" />
                                    <GridColumn field="min" title="Min" filterable={false} editor="numeric" width="75px" />
                                    <GridColumn field="max" title="Max" filterable={false} editor="numeric" width="77px" />
                                    <GridColumn field="regionname" title="Region" editable={false} width="183px" />
                                    <GridColumn cells={{ data: (props) => <ActionCell {...props} /> }} title=" " filterable={false} editable={false} width="50px" />
                                </Grid>
                            </div>
                        ) : null}
                    </div>
                )
            }
    </React.Fragment>
    );
};
export default Inventory;
